import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  Flag,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import {
  useReconciliationRows,
  useGenerateReconciliation,
  useResolveReconciliation,
  useBulkResolveReconciliation,
} from '@/hooks/use-payroll';
import { useProjects } from '@/hooks/use-projects';
import type { TimesheetReconciliation } from '@/lib/types';

const statusColor: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-green-100 text-green-800',
  flagged: 'bg-red-100 text-red-800',
};

export function ReconciliationPage() {
  const [projectId, setProjectId] = useState<number | undefined>();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const { data: projects } = useProjects();
  const { data: rows, isLoading } = useReconciliationRows({
    project: projectId,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    status: statusFilter || undefined,
  });
  const generate = useGenerateReconciliation();
  const resolve = useResolveReconciliation();
  const bulkResolve = useBulkResolveReconciliation();

  const handleGenerate = () => {
    if (!projectId || !dateFrom || !dateTo) return;
    generate.mutate({ project_id: projectId, date_from: dateFrom, date_to: dateTo });
  };

  const handleResolve = (row: TimesheetReconciliation, action: 'confirm' | 'flag') => {
    resolve.mutate({ id: row.id, action });
  };

  const handleBulkResolve = (action: 'confirm' | 'flag') => {
    if (selected.size === 0) return;
    bulkResolve.mutate({ ids: Array.from(selected), action });
    setSelected(new Set());
  };

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (!rows) return;
    if (selected.size === rows.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(rows.map((r) => r.id)));
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" asChild>
            <Link to="/payroll">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Hours Reconciliation
            </h1>
            <p className="text-gray-600 mt-1">
              Compare reported vs scheduled hours per tech per project
            </p>
          </div>
        </div>
      </div>

      {/* Filters + Generate */}
      <Card>
        <CardHeader>
          <CardTitle>Filters &amp; Generate</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div>
              <Label>Project *</Label>
              <select
                className="w-full border rounded-md px-3 py-2 text-sm"
                value={projectId ?? ''}
                onChange={(e) =>
                  setProjectId(e.target.value ? Number(e.target.value) : undefined)
                }
                required
              >
                <option value="">All projects</option>
                {projects?.results?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>From *</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} required />
            </div>
            <div>
              <Label>To *</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} required />
            </div>
            <div>
              <Label>Status</Label>
              <select
                className="w-full border rounded-md px-3 py-2 text-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="flagged">Flagged</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button
              onClick={handleGenerate}
              disabled={generate.isPending}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Generate Rows
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Bulk actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-blue-50 px-4 py-3 rounded-md">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleBulkResolve('confirm')}
            disabled={bulkResolve.isPending}
          >
            <CheckCircle2 className="h-4 w-4 mr-1" />
            Confirm
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleBulkResolve('flag')}
            disabled={bulkResolve.isPending}
          >
            <Flag className="h-4 w-4 mr-1" />
            Flag
          </Button>
        </div>
      )}

      {/* Reconciliation table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 text-center text-gray-500">Loading...</div>
          ) : !rows?.length ? (
            <div className="p-6 text-center text-gray-500">
              No reconciliation rows found. Select a project and date range, then generate.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selected.size === rows.length && rows.length > 0}
                        onChange={toggleAll}
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Tech
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Project
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Scheduled
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Reported
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Diff
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rows.map((row) => {
                    const disc = parseFloat(row.discrepancy);
                    const isDiscrepant = Math.abs(disc) >= 0.5;
                    return (
                      <tr
                        key={row.id}
                        className={isDiscrepant && row.status === 'pending' ? 'bg-yellow-50' : ''}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selected.has(row.id)}
                            onChange={() => toggleSelect(row.id)}
                          />
                        </td>
                        <td className="px-4 py-3 text-sm">{row.date}</td>
                        <td className="px-4 py-3 text-sm">{row.tech_name}</td>
                        <td className="px-4 py-3 text-sm">{row.project_name}</td>
                        <td className="px-4 py-3 text-sm text-right">{row.scheduled_hours}h</td>
                        <td className="px-4 py-3 text-sm text-right">{row.reported_hours}h</td>
                        <td
                          className={`px-4 py-3 text-sm text-right font-medium ${
                            disc > 0 ? 'text-green-600' : disc < 0 ? 'text-red-600' : ''
                          }`}
                        >
                          {disc > 0 ? '+' : ''}
                          {row.discrepancy}h
                          {isDiscrepant && (
                            <AlertTriangle className="inline h-3 w-3 ml-1 text-yellow-500" />
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={statusColor[row.status] ?? ''}>{row.status}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          {row.status === 'pending' && (
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleResolve(row, 'confirm')}
                                disabled={resolve.isPending}
                              >
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleResolve(row, 'flag')}
                                disabled={resolve.isPending}
                              >
                                <Flag className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
